import {
    AfterViewChecked,
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { trim } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { IAppState } from '../../../../../se-modules-administrative-pages/src/lib/execution/models/state.model';
import { setExecutionSearchParams } from '../../../../../se-modules-administrative-pages/src/lib/execution/store/execution.actions';
import { UserConfigurationSettingsService } from '../../services/settings/user-configuration-settings.service';
import { filterListToChip } from '../helpers';
import { SearchFormType } from '../search-page-type.enum';
import { SearchPageService } from '../search-page.service';
import { FromToLabel, SearchParam, SearchParams, SearchParamType, SearchTypeInfo } from '../search-param.model';
import { IAppliedFilterChip } from './types';

@Component({
    selector: 'se-ap-search-filter',
    templateUrl: './ap-search-filter.component.html',
    styleUrls: ['./ap-search-filter.component.scss'],
    standalone: false,
})
export class ApSearchFilterComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    static readonly SearchParamType = SearchParamType;
    readonly SearchParamType = ApSearchFilterComponent.SearchParamType;
    static readonly SearchFormType = SearchFormType;
    readonly SearchFormType = SearchFormType;
    private readonly store = inject<Store<IAppState>>(Store);

    @ViewChild('row')
    rowElement?: ElementRef<HTMLElement>;

    appliedFilterChips: IAppliedFilterChip[] = [];
    searchParamsMapByName: Record<string, SearchParam> = {};

    secondaryFiltersOpen: boolean = false;
    _searchParams: SearchParams = new SearchParams([]);
    paramsGroupedByRow: SearchParam[][] = [];

    rowSize$: BehaviorSubject<number> = new BehaviorSubject(0);

    @Input() set searchParams(params: SearchParams) {
        this._searchParams = params;
        this.paramsGroupedByRow = params.searchParamsFullGroupedByRow;
        this.searchParamsMapByName = Object.fromEntries(
            this.paramsGroupedByRow.flat(2).map((p) => [p.property.name, p]),
        );
        this.setFilterChips();
    }

    get searchParams(): SearchParams {
        return this._searchParams;
    }

    @Input()
    formType!: SearchFormType;
    @Input() isMultiSearchEnabled = true;
    @Input() disableSearch: boolean = false;
    @Input() actualSearchType!: SearchTypeInfo;
    @Input() hideFilters: boolean = false;
    @Input() isBusy: boolean = false;
    @Input() searchTypes: SearchTypeInfo[] = [];
    @Input() searchOnInit: boolean = false;

    @Output() resetAllFilters = new EventEmitter<void>();
    @Output() removeFilter = new EventEmitter<
        string | FromToLabel
    >(); /*potrebbe bastare filter changed che viene scatenato ad ogni modifica del filtro anche quando viene resettato*/
    @Output() applyAllFilters = new EventEmitter<void>();
    @Output() filterChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() searchTypeChanged: EventEmitter<SearchTypeInfo> = new EventEmitter<SearchTypeInfo>();
    @Output() searchRequired: EventEmitter<any> = new EventEmitter<any>();
    @Output() searchParamsChanged: EventEmitter<SearchParams> = new EventEmitter<SearchParams>();

    @HostListener('window:resize', ['$event'])
    onResize(_event: Event) {
        this.setRowSize();
    }

    constructor(
        private searchPageService: SearchPageService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
    ) {}

    ngAfterViewInit() {
        this.searchPageService.loadFormCollections(this.formType);
        if (this.searchOnInit) {
            this.onSearch();
        }
        this.searchParams.clearSearchParamsOnBack();
        //sottoscrivere osservabile search params sourceLoadingCompleted
        this.searchParams.sourceLoadingCompleted$.subscribe((completed) => {
            if (completed) {
                this.setFilterChips();
            }
        });
    }

    ngAfterViewChecked() {
        this.setRowSize();
    }

    ngOnDestroy(): void {
        this.rowSize$.complete();
    }

    setRowSize = () => {
        const width = this.rowElement?.nativeElement.getBoundingClientRect().width;
        if (!!width && this.rowSize$.value !== width) this.rowSize$.next(width);
    };

    setFilterChips() {
        this.appliedFilterChips = [...filterListToChip(Object.values(this.paramsGroupedByRow.slice(1)).flat())];
    }

    isCurrentUserAuthorized = () =>
        this.userConfigurationSettingsService.isAuthorized(['configurationForSite_Set'], 'single');

    removeFilterChip(filterApplied: IAppliedFilterChip) {
        if (this.secondaryFiltersOpen) return;
        const { filterName } = filterApplied;
        this.resetParamToDefault(filterName);
        this.setFilterChips();
        this.removeFilter.emit(filterName);
    }

    setParamValue = (paramName: string, value: any) => {
        this._searchParams.params[paramName] = value;
    };

    resetParamToDefault = (filterName: string) => {
        const searchParam: SearchParam = this.searchParamsMapByName[filterName];
        if (!searchParam) return; //throw error??
        if (searchParam.property.type === SearchParamType.fromTo) {
            searchParam.property.value = {};
        } else {
            searchParam.property.value = searchParam.property.defaultValue;
        }
        this.setParamValue(filterName, searchParam.property.value);
    };

    onSearch() {
        this.searchRequired.emit();
    }

    handleResetAll() {
        this.searchParams.resetSearchParamToDefault();
        this.resetAllFilters.emit();
    }

    onFilterChange(filterName: string, col: number, row: number) {
        switch (filterName) {
            case 'surname':
            case 'name':
            case 'braceletNumber':
            case 'hospitalizationNumber':
            case 'otherPatientId':
            case 'patientId':
            case 'taxCode':
                this.setParamValue(filterName, trim(this.searchParamsMapByName[filterName].property.value));
            default:
                this.setParamValue(filterName, this.searchParamsMapByName[filterName].property.value);
        }
        this.filterChanged.emit({ filterName, col, row });
        this.setFilterChips();
        this.store.dispatch(
            setExecutionSearchParams({ params: this._searchParams.toJSON() as unknown as SearchParams }),
        );
    }

    onResetComboSelectedValue(filterName: string, col: number, row: number) {
        this.resetParamToDefault(filterName);
        this.filterChanged.emit({ filterName, col, row });
        this.setFilterChips();
    }

    onChangeSearchType = (searchType: SearchTypeInfo) => {
        this.searchTypeChanged.emit(searchType);
    };

    onChangeFiltersConfiguration = (filters: SearchParams) => {
        this.searchParams = filters;
        this.searchParamsChanged.emit(filters);
    };
}
